import { Download, RotateCw, X } from "lucide-react";
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";
import { downloadCsv } from "@/shared/utils/csv";
import { colocaToast } from "@/shared/utils/toast";

import { useCryptoSellOrders, useSelectedCryptoSell } from "../../hooks/useCryptoSellData";
import { useCryptoSwapLabels } from "../../hooks/useCryptoSwapLabels";
import type { OtcSellOrder } from "../../types/cryptoSell.types";
import { buildCryptoSellCsv } from "../../utils/cryptoSellCsv";
import { cryptoSwapCsvFilename } from "../../utils/cryptoSwapCsv";
import { SALE_PARAM } from "../../utils/cryptoSwapLinks";
import { CryptoSwapOrdersFilters } from "../orders/CryptoSwapOrdersFilters";
import { CryptoSellOrderDrawer } from "./CryptoSellOrderDrawer";
import { CryptoSellOrdersCards, CryptoSellOrdersTable } from "./CryptoSellOrdersList";
import { CryptoSellStatusChips } from "./CryptoSellStatusChips";

/** The sales tab. The open sale lives in `?sale=`, with the same push/replace rule as the purchases. */
export function SellHistoryPanel() {
  const { t } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const { isInternalUser } = useAuth();
  const sales = useCryptoSellOrders();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedId = searchParams.get(SALE_PARAM);
  const selected = useSelectedCryptoSell(selectedId, sales.rows);

  const openSale = useCallback(
    (sale: OtcSellOrder) => {
      setSearchParams(
        (params) => {
          params.set(SALE_PARAM, sale.id);
          return params;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const closeSale = useCallback(() => {
    setSearchParams(
      (params) => {
        params.delete(SALE_PARAM);
        return params;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const { setFilter } = sales;
  const handleOrganizationChange = useCallback(
    (organizationId: string) => setFilter("organizationId", organizationId),
    [setFilter],
  );

  const handleExport = useCallback(() => {
    if (sales.rows.length === 0) {
      colocaToast.info(t("sell_history_export_empty"));
      return;
    }
    const csv = buildCryptoSellCsv(sales.rows, {
      columns: [
        t("csv_col_id"),
        t("csv_col_created_at"),
        t("csv_col_status"),
        t("sell_csv_col_deposit_amount"),
        t("sell_csv_col_deposit_currency"),
        t("csv_col_network"),
        t("sell_csv_col_deposit_tx"),
        t("csv_col_price"),
        t("sell_csv_col_proceeds"),
        t("sell_csv_col_fee"),
        t("sell_csv_col_credited"),
        t("sell_csv_col_currency"),
        t("sell_csv_col_payout_key"),
        t("sell_csv_col_refund_wallet"),
        t("csv_col_concept"),
        t("sell_csv_col_completed_at"),
        t("sell_csv_col_refunded_at"),
      ],
      status: (sale) => labels.sellStatus(sale.status),
      network: (sale) => labels.network(sale.deposit.network),
    });
    downloadCsv(csv, cryptoSwapCsvFilename(t("sell_csv_filename")));
  }, [sales.rows, labels, t]);

  if (!sales.hasAccess) return <NoAccessView />;

  const pagination = (
    <ColocaPagination
      page={sales.page}
      perPage={sales.pageSize}
      total={sales.total}
      onPageChange={sales.setPage}
      withSeparator={false}
      summary={
        <span className="text-sm text-muted-foreground">
          {t(sales.total === 1 ? "sell_history_count_one" : "sell_history_count_other", {
            count: sales.total,
          })}
        </span>
      }
    />
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <CryptoSellStatusChips
          value={sales.filters.status}
          onChange={(status) => setFilter("status", status)}
        />
        <ColocaButton
          variant="default"
          icon={Download}
          className="shrink-0 self-start"
          onClick={handleExport}
        >
          {t("history_export")}
        </ColocaButton>
      </div>

      {selected.notFound && selectedId && (
        <ColocaAlert
          variant="secondary"
          text={t("sell_drawer_not_found")}
          controls={
            <ColocaIconButton
              variant="ghost"
              size="sm"
              icon={X}
              aria-label={t("drawer_not_found_dismiss")}
              onClick={closeSale}
            />
          }
        />
      )}

      <CryptoSwapOrdersFilters
        filters={sales.filters}
        onRangeChange={sales.setRange}
        onOrganizationChange={isInternalUser ? handleOrganizationChange : undefined}
      />

      {sales.hasError && (
        <ColocaAlert
          variant="destructive"
          text={t("sell_history_load_error")}
          actions={
            <ColocaButton variant="default" icon={RotateCw} onClick={sales.refresh}>
              {t("pairs_retry")}
            </ColocaButton>
          }
        />
      )}

      <div className="hidden lg:block">
        <CryptoSellOrdersTable
          rows={sales.rows}
          isLoading={sales.isLoading}
          isFiltered={sales.isFiltered}
          onOpen={openSale}
          footer={sales.total > sales.pageSize ? pagination : undefined}
        />
      </div>

      <div className="flex flex-col gap-4 lg:hidden">
        <CryptoSellOrdersCards
          rows={sales.rows}
          isLoading={sales.isLoading}
          isFiltered={sales.isFiltered}
          onOpen={openSale}
        />
        {sales.total > sales.pageSize && (
          <ColocaPagination
            page={sales.page}
            perPage={sales.pageSize}
            total={sales.total}
            onPageChange={sales.setPage}
          />
        )}
      </div>

      <CryptoSellOrderDrawer
        sale={selected.order}
        isLoading={selected.isLoading}
        open={!!selectedId && !selected.notFound}
        onOpenChange={(next) => {
          if (!next) closeSale();
        }}
      />
    </div>
  );
}
